import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GitMerge, GitPullRequest, ArrowRight, AlertCircle } from 'lucide-react';

const USERNAME = 'Krishna41357';

/* ── helpers ── */
function repoFromApiUrl(url) {
  return url.replace('https://api.github.com/repos/', '');
}
function githubFromApiUrl(url) {
  return url.replace('https://api.github.com/repos/', 'https://github.com/');
}
function orgName(fullRepo) {
  return fullRepo.split('/')[1] || fullRepo;
}
function initials(repo) {
  const name = repo.split('/')[1] || repo;
  const words = name.replace(/-/g, ' ').replace(/([A-Z])/g, ' $1').trim().split(/\s+/);
  return words.slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || name.slice(0, 2).toUpperCase();
}
function stringColor(str) {
  const palette = ['#7c3aed', '#0891b2', '#059669', '#d97706', '#db2777', '#dc2626', '#2563eb', '#9333ea'];
  let h = 0;
  for (const ch of str) h = ch.charCodeAt(0) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
}

function buildRows(mergedItems, openItems) {
  const map = {};

  const upsert = (pr, status) => {
    const repo = repoFromApiUrl(pr.repository_url);
    if (!map[repo]) {
      map[repo] = {
        repo,
        org:      orgName(repo),
        repoUrl:  githubFromApiUrl(pr.repository_url),
        initials: initials(repo),
        color:    stringColor(repo),
        merged:   0,
        open:     0,
      };
    }
    map[repo][status]++;
  };

  mergedItems.forEach(pr => upsert(pr, 'merged'));
  openItems.forEach(pr   => upsert(pr, 'open'));

  return Object.values(map).sort(
    (a, b) => (b.merged + b.open) - (a.merged + a.open)
  );
}

/* ── Skeleton ── */
function Skeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 py-4 border-b border-black/8 animate-pulse">
          <div className="w-9 h-9 rounded-xl bg-black/8 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-black/8 rounded w-36" />
            <div className="h-2 bg-black/5 rounded w-52" />
          </div>
          <div className="h-4 w-4 rounded bg-black/5" />
        </div>
      ))}
    </>
  );
}

/* ── Animation ── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 10 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.35, delay, ease: [0.22, 1, 0.36, 1] },
});

/* ── Component ── */
export default function OpenSource() {
  const [rows,    setRows]    = useState([]);
  const [totals,  setTotals]  = useState({ repos: 0, merged: 0, open: 0 });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [showAll, setShowAll] = useState(false);

  const LIMIT = 5;
  const visible = showAll ? rows : rows.slice(0, LIMIT);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const headers = { Accept: 'application/vnd.github+json' };

        const [mRes, oRes] = await Promise.all([
          fetch(`https://api.github.com/search/issues?q=author:${USERNAME}+type:pr+is:merged&per_page=50`, { headers }),
          fetch(`https://api.github.com/search/issues?q=author:${USERNAME}+type:pr+is:open&per_page=50`,   { headers }),
        ]);

        if (!mRes.ok || !oRes.ok) {
          const msg = mRes.status === 403 ? 'Rate limited — try again in a minute.' : 'GitHub API error.';
          throw new Error(msg);
        }

        const [mData, oData] = await Promise.all([mRes.json(), oRes.json()]);

        // Filter out the user's own repos (only show external contributions)
        const isOwn = pr => repoFromApiUrl(pr.repository_url)
          .toLowerCase()
          .startsWith(USERNAME.toLowerCase() + '/');

        const mergedItems = (mData.items ?? []).filter(pr => !isOwn(pr));
        const openItems   = (oData.items ?? []).filter(pr => !isOwn(pr));

        if (cancelled) return;

        const built = buildRows(mergedItems, openItems);
        setRows(built);
        setTotals({
          repos:  built.length,
          merged: mergedItems.length,
          open:   openItems.length,
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="opensource"
      className="relative overflow-hidden py-8 sm:py-10 px-3 sm:px-4 lg:px-6 xl:px-8 bg-transparent"
    >
      <div className="relative z-10 max-w-3xl mx-auto">

        {/* ── Header ── */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <motion.p {...fadeUp(0)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-600 font-bold mb-1">
              &lt;/Open Source&gt;
            </motion.p>
            <motion.h1 {...fadeUp(0.04)} className="text-2xl sm:text-3xl font-black text-black">
              Contributions
            </motion.h1>
            {/* Bounty badge */}
            <motion.div {...fadeUp(0.08)} className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-700">
              <span className="text-[9px] font-black font-mono">💰</span>
              <span className="text-[10px] font-bold font-mono">$1,000+ in bounties earned</span>
            </motion.div>
          </div>

          {/* Live stats */}
          <motion.div {...fadeUp(0.08)} className="flex items-center gap-5 pb-0.5">
            {[
              { n: totals.repos,  label: 'repos'  },
              { n: totals.merged, label: 'merged'  },
              { n: totals.open,   label: 'open'    },
            ].map(({ n, label }) => (
              <div key={label} className="text-right">
                <span className="block text-lg font-black text-black leading-none">
                  {loading ? <span className="opacity-30">—</span> : n}
                </span>
                <span className="block text-[9px] font-mono text-black/30 uppercase tracking-wider mt-0.5">
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div {...fadeUp(0.1)} className="h-px bg-black/10" />

        {/* ── Error ── */}
        {error && (
          <div className="flex items-center gap-2 py-6 text-red-500 text-sm font-mono">
            <AlertCircle size={14} className="shrink-0" />
            {error}
          </div>
        )}

        {/* ── Skeleton ── */}
        {loading && !error && <Skeleton />}

        {/* ── Rows ── */}
        {!loading && !error && (
          rows.length === 0
            ? (
              <p className="py-8 text-center text-sm font-mono text-black/30">
                No external contributions found.
              </p>
            )
            : visible.map((c, i) => (
              <motion.a
                key={c.repo}
                href={c.repoUrl}
                target="_blank"
                rel="noreferrer"
                {...fadeUp(0.05 + i * 0.04)}
                className="group flex items-center gap-4 py-4 border-b border-black/8 hover:bg-black/[0.02] transition-colors duration-150 px-1 -mx-1 rounded-sm"
              >
                {/* Avatar */}
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[11px] font-black shrink-0 shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]"
                  style={{ background: c.color }}
                >
                  {c.initials}
                </div>

                {/* Org + repo */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[13px] font-black text-black">{c.org}</span>

                    {c.merged > 0 && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-purple-100 text-purple-700 border border-purple-200">
                        <GitMerge size={8} /> {c.merged}
                      </span>
                    )}
                    {c.open > 0 && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-600 border border-amber-200">
                        <GitPullRequest size={8} /> {c.open}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-black/35 font-mono truncate">{c.repo}</p>
                </div>

                {/* Arrow */}
                <ArrowRight
                  size={13}
                  className="shrink-0 text-black/20 group-hover:text-purple-500 group-hover:translate-x-1 transition-all duration-150"
                />
              </motion.a>
            ))
        )}

          {/* Show more / less */}
          {!loading && !error && rows.length > LIMIT && (
            <button
              onClick={() => setShowAll(v => !v)}
              className="mt-4 w-full py-2.5 text-[11px] font-mono font-bold uppercase tracking-widest text-black/40 hover:text-purple-600 border border-black/8 hover:border-purple-300 rounded-xl transition-all duration-200"
            >
              {showAll ? 'Show less ↑' : `Show ${rows.length - LIMIT} more ↓`}
            </button>
          )}

      </div>
    </section>
  );
}